import React, { useState } from 'react'
import { useIntl } from '@platform/cj-platform-navigation'
import { PageContent } from '@cjdev-internal/visual-stack-x/components/PageContent'
import { Panel, Body } from '@cjdev-internal/visual-stack-x/Panel'
import { Spinner } from '@cjdev-internal/visual-stack-x/Spinner'
import { Button } from '@cjdev-internal/visual-stack-x/Button'
import { Notification } from '@cjdev-internal/visual-stack-x/Notification'
import { DataGrid } from '@cjdev-internal/visual-stack-x/DataGrid'
import type { ColumnDef } from '@cjdev-internal/visual-stack-x/DataGrid'
import { InputField } from '@cjdev-internal/visual-stack-x/components/InputField'
import { Field } from '@cjdev-internal/visual-stack-x/components/Field'
import { DateInput, useDateInput } from '@cjdev-internal/visual-stack-x/components/DateInput'
import { Stack } from '@cjdev-internal/visual-stack-x/Stack'
import messages from '../../messages'
import cjAuth from '../../auth/cjAuth'
import { payoutResultsApi } from '../../services/payoutResultsApi'
import {
  PayoutResultRow,
  PayoutResultsApiConfig,
  PayoutResultsMoreApiConfig,
  PayoutResultsResponse,
} from '../../types'

type FieldErrors = {
  companyId?: string
  payoutDate?: string
}

type PayoutResultsService = {
  query: (config: PayoutResultsApiConfig) => Promise<Response>
  fetchMore: (config: PayoutResultsMoreApiConfig) => Promise<Response>
}

type PayoutResultsProps = {
  service?: PayoutResultsService
}

const toDateString = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const nextDateString = (date: Date): string => {
  const next = new Date(date)
  next.setDate(next.getDate() + 1)
  return toDateString(next)
}

const columnDefs: ColumnDef<PayoutResultRow>[] = [
  { field: 'timestamp', type: 'string', headerName: 'Timestamp', editable: false, width: 220, sort: 'asc' },
  { field: 'message', type: 'string', headerName: 'Message', editable: false, flex: 1, wrapText: true, autoHeight: true, cellStyle: { lineHeight: '1.08', paddingTop: '8px', paddingBottom: '8px' } },
]


const PayoutResults = ({ service = payoutResultsApi }: PayoutResultsProps) => {
  const intl = useIntl()
  const [rows, setRows] = useState<PayoutResultRow[] | null>(null)
  const [queryId, setQueryId] = useState<string | null>(null)
  const [nextToken, setNextToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [companyId, setCompanyId] = useState('')
  const payoutDateInput = useDateInput()
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  const validate = (): FieldErrors => {
    const errors: FieldErrors = {}
    if (companyId && !/^\d{1,10}$/.test(companyId)) {
      errors.companyId = 'Must be numeric digits only (max 10 digits)'
    }
    if (!payoutDateInput.text) {
      errors.payoutDate = 'Required'
    } else if (!payoutDateInput.valid) {
      errors.payoutDate = 'Invalid date'
    }
    return errors
  }

  const errorMessage = async (response: Response): Promise<string> => {
    if (response.status === 408) return intl.formatMessage(messages.labelPayoutResultsTimeout)
    const body = await response.json().catch(() => ({}))
    return body.message || intl.formatMessage(messages.labelPayoutResultsError)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errors = validate()
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    setFieldErrors({})
    setLoading(true)
    setError(null)
    setRows(null)
    setQueryId(null)
    setNextToken(null)

    try {
      const response = await service.query({
        baseUrl: process.env.REACT_APP_ADMIN_TOOL_API_URL || '',
        getBearerToken: cjAuth.getBearerToken,
        companyId,
        startDate: toDateString(payoutDateInput.date!),
        endDate: nextDateString(payoutDateInput.date!),
      })
      if (!response.ok) {
        setError(await errorMessage(response))
        return
      }
      const data: PayoutResultsResponse = await response.json()
      setRows(data.results)
      setQueryId(data.queryId)
      setNextToken(data.nextToken)
    } catch {
      setError(intl.formatMessage(messages.labelPayoutResultsError))
    } finally {
      setLoading(false)
    }
  }

  const handleGetMore = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await service.fetchMore({
        baseUrl: process.env.REACT_APP_ADMIN_TOOL_API_URL || '',
        getBearerToken: cjAuth.getBearerToken,
        queryId: queryId!,
        nextToken,
      })
      if (!response.ok) {
        setError(await errorMessage(response))
        return
      }
      const data: PayoutResultsResponse = await response.json()
      setRows((current) => [...(current || []), ...data.results])
      setNextToken(data.nextToken)
    } catch {
      setError(intl.formatMessage(messages.labelPayoutResultsError))
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageContent>
      <div style={{ padding: '16px' }}>
        <Panel>
          <Body>
              <form onSubmit={handleSubmit}>
                <Stack gap="large" style={{ maxWidth: '400px' }}>
                  <Field
                    label={intl.formatMessage(messages.labelPayoutResultsPayoutDate)}
                    labelFor="payoutDate"
                    error={fieldErrors.payoutDate}
                  >
                    <DateInput
                      {...payoutDateInput}
                      id="payoutDate"
                      error={!!fieldErrors.payoutDate}
                    />
                  </Field>
                  <InputField
                    id="companyId"
                    label={intl.formatMessage(messages.labelPayoutResultsCompanyId)}
                    value={companyId}
                    onChange={setCompanyId}
                    error={fieldErrors.companyId}
                  />
                  <div>
                    <Button type="primary" htmlType="submit" disabled={loading}>
                      {intl.formatMessage(messages.labelPayoutResultsSubmit)}
                    </Button>
                  </div>
                </Stack>
              </form>

              {loading && <Spinner />}

              {error && (
                <Notification type="alert">
                  {error}
                </Notification>
              )}

              {rows !== null && (rows.length === 0 ? (
                <p>{intl.formatMessage(messages.labelPayoutResultsEmpty)}</p>
              ) : (
                <>
                  <div style={{ height: 'calc(100vh - 380px)', minHeight: '300px' }}>
                    <DataGrid<PayoutResultRow>
                      rowData={rows}
                      columnDefs={columnDefs}
                      getRowId={(row) => row.ptr}
                    />
                  </div>
                  {nextToken && (
                    <div>
                      <Button type="secondary" onClick={handleGetMore} disabled={loading}>
                        {intl.formatMessage(messages.labelPayoutResultsGetMore)}
                      </Button>
                    </div>
                  )}
                </>
              ))}
          </Body>
        </Panel>
      </div>
    </PageContent>
  )
}

export default PayoutResults
